/**
 * "My Progress", mockup 5 of 23 September 2026.
 *
 * Top to bottom as drawn: the title with its time range, the three wellness
 * scores over time, the change since the first test, the key markers first
 * against latest by domain, and the closing banner that leads to the full
 * report. Health age, which Brian's dashboard brief moves off the home
 * screen, closes the page in the unit it is measured in.
 *
 * The time range filters the chart, which is the one thing on the page that
 * is a series over time. The improvement card and the marker table say
 * "since your first test" and mean it, so they compare the first and latest
 * draws whatever the chart is showing.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CartesianGrid, Customized, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  Card,
  CardHead,
  DISPLAY,
  DISPLAY_MID,
  Eyebrow,
  PageTitle,
  PortalEmpty,
  PortalError,
  PortalLoading,
  RangeSelect,
  TONE,
  ViewLink,
  formatNumber,
  usePortalQuery,
  type RangeOption,
} from './shell';
import { ArrowDown, ArrowRight, ArrowUp, BarsLine, Flask, LeafSolid } from './icons';

/* ------------------------------------------------------------------ *
 * What the API sends (GET /portal/progress)
 * ------------------------------------------------------------------ */

type Domain = 'metabolic' | 'inflammation' | 'liver';
type MarkerTab = Domain | 'body';

interface ScorePoint {
  reportId: string;
  at: string;
  metabolic: number | null;
  inflammation: number | null;
  liver: number | null;
}

interface ScoreChange {
  first: number;
  latest: number;
  change: number;
}

interface MarkerRow {
  key: string;
  label: string;
  first: number | null;
  latest: number | null;
  change: number | null;
  quality: 'improved' | 'worsened' | 'steady' | null;
  decimals: number;
}

export interface PortalProgressData {
  scores: ScorePoint[];
  improvement: Record<Domain, ScoreChange | null> | null;
  markers: Record<MarkerTab, MarkerRow[]>;
  firstTestAt: string | null;
  latestTestAt: string | null;
  latestReportId: string | null;
  bodyScanDates: { first: string | null; latest: string | null };
  healthAge: {
    healthAge: number | null;
    chronologicalAge: number | null;
    differenceLabel: string | null;
    message: string | null;
  } | null;
}

/* ------------------------------------------------------------------ *
 * Constants from the mockup
 * ------------------------------------------------------------------ */

const LINES: Array<{ key: Domain; label: string; colour: string; text: string }> = [
  { key: 'metabolic', label: 'Metabolic Score', colour: '#0b4a3a', text: '#0c382a' },
  { key: 'inflammation', label: 'Inflammation Score', colour: '#c0875a', text: '#9a6545' },
  { key: 'liver', label: 'Liver Health Score', colour: '#4a9ad6', text: '#3f78ae' },
];

type Range = 'last3' | 'last6' | 'all';

const RANGES: Array<RangeOption<Range>> = [
  { value: 'last3', label: 'Last 3 Months' },
  { value: 'last6', label: 'Last 6 Months' },
  { value: 'all', label: 'All Time' },
];

const TABS: Array<{ key: MarkerTab; label: string }> = [
  { key: 'metabolic', label: 'Metabolic' },
  { key: 'inflammation', label: 'Inflammation' },
  { key: 'liver', label: 'Liver Health' },
  { key: 'body', label: 'Body Composition' },
];

/** Draws on or after this moment are inside the range. */
export function rangeStart(range: Range, now = new Date()): Date | null {
  if (range === 'all') return null;
  const start = new Date(now.getTime());
  start.setMonth(start.getMonth() - (range === 'last3' ? 3 : 6));
  return start;
}

/**
 * Short month names for the x axis, as drawn ("Oct", "Nov"). Two draws in
 * one month would print the same label twice, so then every label carries
 * its day as well.
 */
export function axisLabels(dates: string[]): string[] {
  const month = (value: string) => new Date(value).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  const months = dates.map(month);
  if (new Set(months).size === months.length) return months;
  return dates.map((value) =>
    new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
  );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export function PortalProgress() {
  const { data, isLoading, error, refetch } = usePortalQuery<PortalProgressData>('progress', '/progress');
  const [range, setRange] = useState<Range>('last6');
  const [tab, setTab] = useState<MarkerTab>('metabolic');

  const inRange = useMemo(() => {
    const start = rangeStart(range);
    return (data?.scores ?? []).filter((point) => !start || new Date(point.at).getTime() >= start.getTime());
  }, [data, range]);

  if (isLoading) return <PortalLoading />;
  if (error) {
    return (
      <PortalError
        message={error instanceof Error ? error.message : 'Could not load your progress'}
        retry={() => void refetch()}
      />
    );
  }
  if (!data) return null;

  const reportLink = data.latestReportId ? `/portal/reports/${data.latestReportId}` : null;

  if (data.scores.length === 0 && data.markers.body.length === 0) {
    return (
      <>
        <PageTitle title="My Progress" subtitle="Track your results. See your trends. Build a healthier tomorrow." />
        <PortalEmpty
          title="Nothing to chart yet"
          body="Your progress appears here once your first results have been reviewed and released by your clinician."
        />
      </>
    );
  }

  return (
    <div className="space-y-[8px] pb-1">
      <PageTitle
        title="My Progress"
        size={30}
        subtitleSize={12}
        subtitle="Track your results. See your trends. Build a healthier tomorrow."
        aside={<RangeSelect label="Time range for the chart" value={range} options={RANGES} onChange={setRange} />}
      />

      {/* 1. Scores over time. */}
      <Card className="px-[15px] pb-[13px] pt-[15px]">
        <Eyebrow strong>Your wellness scores over time</Eyebrow>
        {inRange.length === 0 ? (
          <p className="py-10 text-center text-[13px]" style={{ color: TONE.body }}>
            No blood draws in this period. Choose a longer range to see your earlier results.
          </p>
        ) : (
          <WellnessChart points={inRange} />
        )}
        <ul className="mt-[10px] flex flex-wrap items-center justify-center gap-x-[14px] gap-y-1">
          {LINES.map((line) => (
            <li key={line.key} className="flex items-center gap-[6px] whitespace-nowrap text-[10.5px]" style={{ color: '#26303f' }}>
              <span aria-hidden="true" className="h-[9px] w-[9px] rounded-full" style={{ background: line.colour }} />
              {line.label}
            </li>
          ))}
        </ul>
      </Card>

      {/* 2. Change since the first test. */}
      <Card className="px-[15px] pb-[14px] pt-[12px]">
        <div className="flex items-start gap-[16px]">
          <span className="mt-[1px] shrink-0" style={{ color: '#0b4a3a' }}>
            <BarsLine size={24} strokeWidth={1.6} />
          </span>
          <div className="min-w-0">
            <Eyebrow strong className="mt-[3px]">
              Your improvement
            </Eyebrow>
            <p className="mt-[6px] text-[12.5px] leading-tight" style={{ color: TONE.body }}>
              Change since your first test
            </p>
          </div>
        </div>
        {data.improvement ? (
          <div className="mt-[14px] grid grid-cols-3">
            {LINES.map((line, index) => (
              <ImprovementColumn
                key={line.key}
                label={line.label}
                colour={line.text}
                change={data.improvement![line.key]}
                divider={index > 0}
              />
            ))}
          </div>
        ) : (
          <p className="mt-[12px] text-[13px] leading-relaxed" style={{ color: TONE.body }}>
            One blood draw so far. Your change appears after your next draw, measured against this one.
          </p>
        )}
      </Card>

      {/* 3. Key markers, first test against latest. */}
      <Card className="px-[15px] pb-[6px] pt-[12px]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-[14px]">
            <span className="shrink-0" style={{ color: TONE.heading }}>
              <Flask size={24} strokeWidth={1.6} />
            </span>
            <Eyebrow strong>Key markers trend</Eyebrow>
          </div>
          {reportLink && <ViewLink to={reportLink}>View All Labs</ViewLink>}
        </div>

        <div role="tablist" aria-label="Marker group" className="-mx-1 mt-[12px] flex gap-[7px] overflow-x-auto px-1 pb-[2px]">
          {TABS.map((option) => {
            const active = option.key === tab;
            return (
              <button
                key={option.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(option.key)}
                className="h-[21px] shrink-0 whitespace-nowrap rounded-[7px] px-[9px] text-[10px] leading-none transition-colors"
                style={
                  active
                    ? { background: 'var(--forest)', color: '#fff', fontWeight: 500 }
                    : { background: '#f1f0ec', color: '#2e3746' }
                }
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <MarkerTable rows={data.markers[tab]} body={tab === 'body'} />
      </Card>

      {/* 4. The closing banner. */}
      <div className="flex items-center gap-[10px] rounded-[9px] px-[13px] py-[12px]" style={{ background: '#eaefe9' }}>
        <span className="shrink-0 text-forest">
          <LeafSolid size={27} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-[12px] italic leading-tight" style={{ color: '#0a0f14', fontWeight: 420 }}>
            Consistency creates real change.
          </p>
          <p className="mt-[4px] text-[10px] leading-snug" style={{ color: '#2e363a' }}>
            Your daily choices are building a healthier, stronger you.
          </p>
        </div>
        {reportLink && (
          <Link
            to={reportLink}
            className="inline-flex h-[29px] shrink-0 items-center gap-[4px] rounded-[7px] px-[8px] text-[10.5px] font-medium text-white transition-colors hover:bg-forest-deep"
            style={{ background: '#104d39' }}
          >
            View Full Report
            <ArrowRight size={12} strokeWidth={2.1} />
          </Link>
        )}
      </div>

      {/* 5. Health age, moved here from the dashboard. */}
      {data.healthAge && (data.healthAge.healthAge !== null || data.healthAge.message) && (
        <Card className="px-[15px] py-[12px]">
          <CardHead label="Your health age" />
          {data.healthAge.healthAge !== null ? (
            <div className="mt-[8px] flex items-end justify-between gap-3">
              <p className="font-display text-[24px] font-semibold leading-none" style={{ ...DISPLAY, color: TONE.heading }}>
                {data.healthAge.healthAge}
                <span className="ml-1.5 font-sans text-[12px] font-normal" style={{ color: TONE.body }}>
                  years
                </span>
              </p>
              <div className="text-right">
                {data.healthAge.chronologicalAge !== null && (
                  <p className="text-[11.5px]" style={{ color: TONE.body }}>
                    Your actual age is {data.healthAge.chronologicalAge}
                  </p>
                )}
                {data.healthAge.differenceLabel && (
                  <span
                    className="mt-[4px] inline-block rounded-full px-[9px] py-[3px] text-[10.5px] font-medium"
                    style={{ background: '#e7f1ec', color: TONE.forestText }}
                  >
                    {data.healthAge.differenceLabel}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className="mt-[8px] text-[12.5px] leading-relaxed" style={{ color: TONE.body }}>
              {data.healthAge.message}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The chart
 * ------------------------------------------------------------------ */

interface ChartRow {
  label: string;
  metabolic: number | null;
  inflammation: number | null;
  liver: number | null;
}

/**
 * The latest value of each line, in a small coloured tag at its right end.
 *
 * Drawn from the chart's own scales, so a tag sits exactly where its line
 * ends. When two lines end close together their tags are pushed apart and a
 * short leader keeps each tied to its line, as the mockup draws them.
 */
function EndTags(props: Record<string, unknown>) {
  const rows = props.data as ChartRow[] | undefined;
  const xAxis = (props.xAxisMap as Record<string, { scale: ((value: string) => number) & { bandwidth?: () => number } }> | undefined)?.[0];
  const yAxis = (props.yAxisMap as Record<string, { scale: (value: number) => number }> | undefined)?.[0];
  const offset = props.offset as { top: number; left: number; width: number; height: number } | undefined;
  if (!rows || rows.length === 0 || !xAxis || !yAxis || !offset) return null;

  const last = rows[rows.length - 1]!;
  const x = xAxis.scale(last.label) + (xAxis.scale.bandwidth ? xAxis.scale.bandwidth() / 2 : 0);

  const tags = LINES.flatMap((line) => {
    const value = last[line.key];
    return value === null ? [] : [{ ...line, value, y: yAxis.scale(value), tagY: yAxis.scale(value) }];
  }).sort((a, b) => a.y - b.y);

  const height = 14;
  const gap = 3;
  for (let index = 1; index < tags.length; index += 1) {
    const previous = tags[index - 1]!;
    const tag = tags[index]!;
    if (tag.tagY - previous.tagY < height + gap) tag.tagY = previous.tagY + height + gap;
  }
  // Keep the stack inside the plot: pull it up if it ran off the bottom.
  const bottom = offset.top + offset.height;
  const overflow = (tags[tags.length - 1]?.tagY ?? 0) + height / 2 - bottom;
  if (overflow > 0) for (const tag of tags) tag.tagY -= overflow;

  const tagX = x + 9;
  return (
    <g>
      {tags.map((tag) => {
        const text = String(tag.value);
        const width = 11 + text.length * 6.2;
        return (
          <g key={tag.key}>
            {Math.abs(tag.tagY - tag.y) > 1 && (
              <line x1={x + 3} y1={tag.y} x2={tagX} y2={tag.tagY} stroke={tag.colour} strokeWidth={1} opacity={0.7} />
            )}
            <rect x={tagX} y={tag.tagY - height / 2} width={width} height={height} rx={3.5} fill={tag.colour} />
            <text
              x={tagX + width / 2}
              y={tag.tagY + 3.6}
              textAnchor="middle"
              fill="#fff"
              style={{ fontSize: 10, fontWeight: 500, fontFamily: 'IBM Plex Sans, system-ui, sans-serif' }}
            >
              {text}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function WellnessChart({ points }: { points: ScorePoint[] }) {
  const labels = axisLabels(points.map((point) => point.at));
  const rows: ChartRow[] = points.map((point, index) => ({
    label: labels[index]!,
    metabolic: point.metabolic,
    inflammation: point.inflammation,
    liver: point.liver,
  }));

  return (
    <div className="mt-[10px] h-[136px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 16, right: 36, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#ececea" strokeWidth={0.8} />
          <XAxis
            dataKey="label"
            interval={0}
            tickLine={false}
            axisLine={{ stroke: '#d8d8d6' }}
            tick={{ fontSize: 10.5, fill: TONE.faint }}
            tickMargin={8}
            padding={{ left: 12, right: 4 }}
            height={26}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickLine={false}
            axisLine={{ stroke: '#d8d8d6' }}
            tick={{ fontSize: 10.5, fill: TONE.faint }}
            tickMargin={6}
            width={32}
          />
          <Tooltip
            cursor={{ stroke: '#d8d8d6', strokeWidth: 1 }}
            contentStyle={{ borderRadius: 8, border: `1px solid ${TONE.line}`, background: '#fff', fontSize: 12, padding: '6px 10px' }}
            labelStyle={{ color: TONE.heading, fontWeight: 600, marginBottom: 2 }}
            formatter={(value: number, name: string) => [value, LINES.find((line) => line.key === name)?.label ?? name]}
          />
          {LINES.map((line) => (
            <Line
              key={line.key}
              type="linear"
              dataKey={line.key}
              stroke={line.colour}
              strokeWidth={2}
              dot={{ r: 3.3, fill: line.colour, stroke: line.colour, strokeWidth: 0 }}
              activeDot={{ r: 4.5, fill: line.colour, stroke: '#fff', strokeWidth: 1.5 }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
          <Customized component={EndTags} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Improvement and markers
 * ------------------------------------------------------------------ */

function ImprovementColumn({
  label,
  colour,
  change,
  divider,
}: {
  label: string;
  colour: string;
  change: ScoreChange | null;
  divider: boolean;
}) {
  return (
    <div
      className={`flex min-w-0 flex-col items-center px-1 text-center ${divider ? 'border-l' : ''}`}
      style={{ borderColor: '#e6e6e3' }}
    >
      <p className="whitespace-nowrap text-[clamp(10px,2.95vw,11.5px)] leading-tight" style={{ color: '#161d29' }}>
        {label}
      </p>
      {change ? (
        <>
          <p className="mt-[10px] flex items-center gap-[7px] leading-none" style={{ color: colour }}>
            {change.change > 0 && <ArrowUp size={21} strokeWidth={1.9} />}
            {change.change < 0 && <ArrowDown size={21} strokeWidth={1.9} />}
            <span className="font-display text-[27px] font-semibold" style={DISPLAY}>
              {change.change === 0 ? '0' : Math.abs(change.change)}
            </span>
          </p>
          <p className="mt-[9px] text-[11.5px] leading-none" style={{ color: '#3a4556' }}>
            from {change.first} to {change.latest}
          </p>
        </>
      ) : (
        <p className="mt-[12px] text-[11.5px]" style={{ color: TONE.faint }}>
          Not scored on both tests
        </p>
      )}
    </div>
  );
}

function ChangeCell({ row }: { row: MarkerRow }) {
  if (row.change === null) return <span style={{ color: TONE.faint }}>-</span>;
  const colour =
    row.quality === 'improved' ? TONE.forestText : row.quality === 'worsened' ? 'var(--alert-red)' : '#5b6472';
  const word = row.quality === 'improved' ? 'better' : row.quality === 'worsened' ? 'worse' : 'steady';
  return (
    <span
      className="inline-flex items-center gap-[8px] font-medium"
      style={{ color: colour }}
      aria-label={`${row.change < 0 ? 'Down' : row.change > 0 ? 'Up' : 'No change'} ${formatNumber(Math.abs(row.change), row.decimals)}, ${word}`}
    >
      {row.change < 0 && <ArrowDown size={13} strokeWidth={2.1} />}
      {row.change > 0 && <ArrowUp size={13} strokeWidth={2.1} />}
      {row.change === 0 ? '0' : formatNumber(Math.abs(row.change), row.decimals)}
    </span>
  );
}

function MarkerTable({ rows, body }: { rows: MarkerRow[]; body: boolean }) {
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-[12.5px] leading-relaxed" style={{ color: TONE.body }}>
        {body
          ? 'Your body composition appears here after your first scan.'
          : 'None of these markers was measured on your blood draws yet.'}
      </p>
    );
  }
  const columns = 'grid grid-cols-[39fr_20fr_26fr_15fr] items-center gap-x-1';
  return (
    <div className="mt-[10px] text-[10.5px]" style={{ color: '#0d1018' }}>
      <div className={`${columns} border-b pb-[8px] font-medium`} style={{ borderColor: '#e7e6e2' }}>
        <span>Marker</span>
        <span>{body ? 'First Scan' : 'First Test'}</span>
        <span>{body ? 'Latest Scan' : 'Latest Test'}</span>
        <span>Change</span>
      </div>
      {rows.map((row) => (
        <div key={row.key} className={`${columns} min-h-[26px] border-b py-[4px]`} style={{ borderColor: '#e7e6e2' }}>
          <span className="pr-1 leading-tight">{row.label}</span>
          <span>{row.first === null ? '-' : formatNumber(row.first, row.decimals)}</span>
          <span>{row.latest === null ? '-' : formatNumber(row.latest, row.decimals)}</span>
          <ChangeCell row={row} />
        </div>
      ))}
    </div>
  );
}
